import { detect } from 'detect-browser';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { ProfileRelatedInfoBean } from 'src/app/beans/account/account-module.bean';
import { DataCarrierService } from 'src/app/services/core/data-carrier.service';

import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSelectChange } from '@angular/material/select';

import { HealthRecordService } from '../../../services/health-record/health-record.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import {
    ValidationUtilityService
} from '../../shared/utils/validation.util/validation-utility.service';
import { Lookup, RecordsReleaseRequest } from '../record-release.type';

const ValidShareList: ValidatorFn = (fg: FormGroup) => {
	const method: string = fg.get('recordSharingMethod').value;
	const email = fg.get('providerEmail').value;
	const fax = fg.get('providerFaxNumber').value;

	if (method === 'Fax' && fax === '') {
		return { fax: true };
	} else if (method === 'Secure Email' && email !== '') {
		return { email: true };
	} else {
		return null;
	}
};

@Component({
	selector: 'app-record-release',
	templateUrl: './record-release-modal.component.html',
	styleUrls: ['./record-release-modal.component.scss'],
})
export class RecordReleaseModalComponent implements OnInit, OnDestroy {
	recordReleaseForm: FormGroup;
	recordReleaseControlArray: AbstractControl[] = [];
	showOther: boolean;
	showEmail: boolean;
	showFax: boolean;
	showMailingAdress: boolean;
	authorizationPurpose: Lookup[] = [];
	inclusionTypes: Lookup[] = [];
	isMobileView: boolean = false;
	isSignatureMissingError: boolean = false;
	reportInclusions = [];
	reportTypes = [];
	sharingMethods: Lookup[] = [];
	states = [];
	profileRelatedInfo: ProfileRelatedInfoBean;
	patientSignature = '';
	patientAuthorizationDate = new Date();
	
	subscriptions: Subscription[] = [];

	@HostListener('window:resize') onResize() {
		this.updateIsMobileView();
	}

	updateIsMobileView(): void {
		if (window.innerWidth < 800) {
			this.isMobileView = true;
		} else {
			this.isMobileView = false;
		}
	}

	constructor(
		private healthRecordService: HealthRecordService,
		private dialogRef: MatDialogRef<RecordReleaseModalComponent>,
		private dataCarrierService: DataCarrierService,
		private spinner: SpinnerComponent,
		private fb: FormBuilder,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.updateIsMobileView();
		this.getAuthorizationPurpose();
		this.getInclusionTypes();
		this.getSharingMethods();
		this.getStates();
		this.processReportTypes();
		this.profileRelatedInfo = this.dataCarrierService.getChannel().getValue().profile;
	}

	ngOnInit(): void {
		this.initForm();

		setTimeout(() => {
			if ((this.recordReleaseControlArray[0] as any)?.nativeElement) {
				((this.recordReleaseControlArray[0] as any)?.nativeElement as HTMLElement).focus();
				this.scroll((this.recordReleaseControlArray[0] as any)?.nativeElement as HTMLElement);
			}
		}, 0);
	}

	initForm(): void {
		this.recordReleaseForm = this.fb.group({
			patientLastName: ['', Validators.required],
			patientFirstName: ['', Validators.required],
			patientMiddleName: [''],
			patientDob: ['', Validators.required],
			patientAddress1: ['', Validators.required],
			patientAddress2: [''],
			patientCity: ['', Validators.required],
			patientState: ['', Validators.required],
			patientZipCode: ['', [Validators.required, ValidationUtilityService.zipCodeValidator]],
			patientReportsList: [[], Validators.required],
			patientReportInclusionsOption: ['', Validators.required],
			patientReportInclusionsList: [[]],
			providerLastName: ['', Validators.required],
			providerFirstName: ['', Validators.required],
			providerMiddleName: [''],
			providerType: ['', Validators.required],
			providerAddress1: ['', Validators.required],
			providerAddress2: [''],
			providerCity: ['', Validators.required],
			providerState: ['', Validators.required],
			providerZipCode: ['', [Validators.required, ValidationUtilityService.zipCodeValidator]],
			providerPhoneNumber: ['', [Validators.required, ValidationUtilityService.phoneNumberValidator]],
			providerFaxNumber: [''],
			providerEmail: [''],
			mailingAddress1: [''],
			mailingAddress2: [''],
			mailingCity: [''],
			mailingState: [''],
			mailingZipCode: [''],
			recordSharingMethod: ['', Validators.required],
			recordAuthorizationPurpose: [''],
			recordAuthorizationPurposeOther: ['']
		}, {
			validators: ValidShareList
		});

		// add all the controls to the control array
		Object.keys(this.recordReleaseForm.controls).forEach(key => {
			this.recordReleaseControlArray.push(this.recordReleaseForm.get(key));
		});
	}

	inclusionSelection(item):void {
		const reportInclusions: number[] = this.recordReleaseForm.controls.patientReportInclusionsList.value;
		const index = reportInclusions.findIndex(x => x === item.id);

		if (index === -1) {
			reportInclusions.push(item.id);
		} else {
			reportInclusions.splice(index, 1);
		}
		this.recordReleaseForm.controls.patientReportInclusionsList.setValue(reportInclusions);
	}

	getAction(actionType): void {
		if (actionType.value === 'submit') {
			this.submit();
		} else {
			this.dialogRef.close();
		}
	}

	getAuthorizationPurpose(): void {
		const authorizationPurposeSubscription = this.healthRecordService.getAuthorizationPurpose()
			.subscribe({
				next: (response) => {
					this.authorizationPurpose = response;
				},
				error: (error) => {
					console.error(`[${RecordReleaseModalComponent.name}][${this.getAuthorizationPurpose.name}]`, error);
				}
			});
		this.subscriptions.push(authorizationPurposeSubscription);
	}

	getInclusionTypes(): void {
		const inclusionSubscription = this.healthRecordService.getInclusions()
			.subscribe({
				next: (response) => {
					this.inclusionTypes = response;
				},
				error: (error) => {
					console.error(`[${RecordReleaseModalComponent.name}][${this.getInclusionTypes.name}]`, error);
				}
			});
		this.subscriptions.push(inclusionSubscription);
	}

	getSignature(signature): void {
		this.patientSignature = signature;
	}

	getSharingMethods(): void {
		const sharingMethodsSubscription = this.healthRecordService.getSharingMethods()
			.subscribe({
				next: (response) => {
					this.sharingMethods = response;
				},
				error: (error) => {
					console.error(`[${RecordReleaseModalComponent.name}][${this.getSharingMethods.name}]`, error);
				}
			});
		this.subscriptions.push(sharingMethodsSubscription);
	}

	getStates(): void {
		const stateSubscription = this.healthRecordService.getStates()
			.subscribe({
				next: (response) => {
					this.states = response;
				},
				error: (error) => {
					console.error(`[${RecordReleaseModalComponent.name}][${this.getStates.name}]`, error);
				}
			})
			this.subscriptions.push(stateSubscription);
	}

	onInclusionOptionChange(event: MatRadioChange): void {
		if(event.value) {
			this.recordReleaseForm.controls.patientReportInclusionsList.addValidators(Validators.required);
		} else {
			this.recordReleaseForm.controls.patientReportInclusionsList.clearValidators();
		}
	}

	onPurposeChange(event: MatSelectChange): void {
		const otherValue = this.authorizationPurpose.find(purpose => purpose.name === 'Other');
		this.showOther = event.value === otherValue?.id;
		if (this.showOther) {
			this.recordReleaseForm.controls.recordAuthorizationPurposeOther.addValidators(Validators.required);
		} else {
			this.recordReleaseForm.controls.recordAuthorizationPurposeOther.clearValidators();
		}
		this.recordReleaseForm.controls.recordAuthorizationPurposeOther.updateValueAndValidity();
	}

	methodForSharing(shareList: MatSelectChange): void {
		this.recordReleaseForm.controls.providerFaxNumber.clearValidators();
		this.recordReleaseForm.controls.providerEmail.clearValidators();
		this.recordReleaseForm.controls.mailingAddress1.clearValidators();
		this.recordReleaseForm.controls.mailingCity.clearValidators();
		this.recordReleaseForm.controls.mailingState.clearValidators();
		this.recordReleaseForm.controls.mailingZipCode.clearValidators();
		const faxMethod = this.sharingMethods.find(method => method.name === 'Fax');
		const emailMethod = this.sharingMethods.find(method => method.name === 'Secure Email');
		const usMailMethod = this.sharingMethods.find(method => method.name === 'US Mail');
		this.showFax = false;
		this.showEmail = false;
		this.showMailingAdress = false;

		if (shareList.value === faxMethod?.id) {
			this.recordReleaseForm.controls.providerFaxNumber.addValidators([
				Validators.required,
				ValidationUtilityService.phoneNumberValidator
			]);
			this.showFax = true;
		} else if (shareList.value === emailMethod?.id) {
			this.recordReleaseForm.controls.providerEmail.addValidators([
				Validators.required,
				ValidationUtilityService.emailValidator,
			]);
			this.showEmail = true;
		} else if (shareList.value === usMailMethod?.id) {
			this.recordReleaseForm.controls.mailingAddress1.addValidators(Validators.required);
			this.recordReleaseForm.controls.mailingCity.addValidators(Validators.required);
			this.recordReleaseForm.controls.mailingState.addValidators(Validators.required);
			this.recordReleaseForm.controls.mailingZipCode.addValidators([
				Validators.required,
				ValidationUtilityService.zipCodeValidator
			]);
			this.showMailingAdress = true;
		}

		this.recordReleaseForm.controls.providerFaxNumber.updateValueAndValidity();
		this.recordReleaseForm.controls.providerEmail.updateValueAndValidity();
		this.recordReleaseForm.controls.mailingAddress1.updateValueAndValidity();
		this.recordReleaseForm.controls.mailingCity.updateValueAndValidity();
		this.recordReleaseForm.controls.mailingState.updateValueAndValidity();
		this.recordReleaseForm.controls.mailingZipCode.updateValueAndValidity();
	}

	isFormFieldsValid(): boolean {
		if (!this.recordReleaseForm?.touched) {
			return true;
		}
		return this.recordReleaseControlArray.every(control => {
			if (control.touched) {
				return control.valid;
			}
			return true;
		});
	}

	processReportTypes(): void {
		this.reportTypes = this.data.reports.map(report => {
			return report.report.map(r => {
				const formattedDate = moment(r.contextDate).format('MMM YYYY')
				return `${r.documentName} - ${formattedDate}`;
			});
		}).flat();
	}

	setMailingAddress(event): void {
		if(event.checked) {
			// copy the values from provider address to mailing address
			this.recordReleaseForm.controls.mailingAddress1.setValue(this.recordReleaseForm.controls.providerAddress1.value);
			this.recordReleaseForm.controls.mailingAddress2.setValue(this.recordReleaseForm.controls.providerAddress2.value);
			this.recordReleaseForm.controls.mailingCity.setValue(this.recordReleaseForm.controls.providerCity.value);
			this.recordReleaseForm.controls.mailingState.setValue(this.recordReleaseForm.controls.providerState.value);
			this.recordReleaseForm.controls.mailingZipCode.setValue(this.recordReleaseForm.controls.providerZipCode.value);

			// make the fields readonly
			this.recordReleaseForm.controls.mailingAddress1.disable();
			this.recordReleaseForm.controls.mailingAddress2.disable();
			this.recordReleaseForm.controls.mailingCity.disable();
			this.recordReleaseForm.controls.mailingState.disable();
			this.recordReleaseForm.controls.mailingZipCode.disable();
		} else {
			//enable the fields
			this.recordReleaseForm.controls.mailingAddress1.enable();
			this.recordReleaseForm.controls.mailingAddress2.enable();
			this.recordReleaseForm.controls.mailingCity.enable();
			this.recordReleaseForm.controls.mailingState.enable();
			this.recordReleaseForm.controls.mailingZipCode.enable();
		}
	}

	submit(): void {
		if(this.recordReleaseForm.controls.patientReportInclusionsOption.value && this.recordReleaseForm.controls.patientReportInclusionsList.value.length === 0) {
			this.recordReleaseForm.controls.patientReportInclusionsList.setErrors({ required: true });
		}
		if(this.patientSignature.length === 0) {
			this.isSignatureMissingError = true;
			this.recordReleaseForm.setErrors({ signature: true });
		} else {
			this.isSignatureMissingError = false;
			if(this.recordReleaseForm.hasError('signature')) {
				this.recordReleaseForm.setErrors(null);
			}
		}

		if (this.recordReleaseForm.valid) {
			if (
				this.recordReleaseForm.value.recordSharingMethod &&
				typeof this.recordReleaseForm.value.recordSharingMethod === 'string'
			) {
				this.recordReleaseForm.value.recordSharingMethod = [this.recordReleaseForm.value.recordSharingMethod];
			}

			this.recordReleaseForm.controls.mailingAddress1.enable();
			this.recordReleaseForm.controls.mailingAddress2.enable();
			this.recordReleaseForm.controls.mailingCity.enable();
			this.recordReleaseForm.controls.mailingState.enable();
			this.recordReleaseForm.controls.mailingZipCode.enable();
			const data: RecordsReleaseRequest = this.recordReleaseForm.value;
			data.patientGender = this.profileRelatedInfo.profileInfo.gender;
			data.uuid = this.profileRelatedInfo.profileInfo.uuid;
			data.patientAuthorizationDate = new Date().toISOString();
			data.patientEmail = this.profileRelatedInfo.profileInfo.email;
			data.patientKeycode = this.profileRelatedInfo.profileInfo.patientID;
			data.patientSignature = this.patientSignature;

			this.spinner.startLoader();
			this.healthRecordService.submitRecordRelease(data).subscribe(() => {
				sessionStorage.setItem('submitRecordRelease', '1');
				this.dialogRef.close();
				this.spinner.stopLoader();
			});
		} else {
			this.recordReleaseForm.markAllAsTouched();
			this.recordReleaseControlArray.some(control => {
				if (control.invalid) {
					if ((control as any).nativeElement) {
						((control as any).nativeElement as HTMLElement).focus();
						this.scroll((control as any).nativeElement as HTMLElement);
					}
					return true;
				}
				return false;
			});
		}
	}

	scroll(target): boolean {
		let element;
		if (target instanceof HTMLElement) {
			element = target;
		} else {
			element = document.getElementById(target) as HTMLElement;
		}
		if (element !== null) {
			element.focus();
			const browser = detect();
			switch (browser && browser.name) {
				case 'edge':
				case 'ie':
					element.scrollIntoView({
						block: 'start',
						inline: 'start',
					});
					break;
				default:
					element.scrollIntoView({ behavior: 'smooth', block: 'center' });
					break;
			}
			return true;
		}
		return false;
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
